import React, { useState, useContext, useEffect } from 'react'
import { useDropzone } from 'react-dropzone'
import csv from 'csvtojson'
import XLSX from 'xlsx'
import moment from 'moment'
import _ from 'lodash'
import Table from '../../../dpe-usco/components/Table/Table'
import TableStatus from '../../../components/subComponents/TableStatus/TableStatus'
import { Button, Modal } from 'syngenta-react-components'
import { getISODateFromExcel } from '../../../services/utils'
import { initStructure, headerCell } from './AcceptableHeaders'
import { ACCEPTABLE_HEADERS, REQUIRED_HEADERS } from './AcceptableHeaders'
import { AuthContext } from '../../../services/context'
import AccessDeniedView from '../../Interstitial/AccessDeniedView'
import error from '../../../assets/icons/error.svg'
import error_outline from '../../../assets/icons/error_outline.svg'
import { LoadingModal, ServerErrorModal, SuccessModal, WarningModal, WrongFormatModal } from './Models'
import { verifyAccountLeads } from '../../../services/apiHelper'


const DataUploadingModal = ({ showModal, setShowModal, data, closeModel, hasUpload }) => {
  const [isLoading, setIsLoading] = useState('loading')
  const [filterData, setFilteredData] = useState({
    reseller_sap_ids: [],
    new_account_lead_emails: []
  })
  const [arrOfNoEmailFound, setArrOfNoEmailFound] = useState([])
  const [arrOfNoResellerSapIDFound, setArrOfNoResellerSapIDFound] = useState([])

  useEffect(() => {
    const filteredResellerId = [...new Set(data.map(curr => curr.reseller_sap_id))]
    const filteredEmail = [...new Set(data.map(curr => curr.new_account_lead_email))]

    const payload = {
      reseller_sap_ids: filteredResellerId,
      new_account_lead_emails: filteredEmail
    }

    setFilteredData(payload)

    if (payload.reseller_sap_ids.length > 0 || payload.new_account_lead_emails.length > 0) {
      verifyPostedData(payload)
    }
  }, [data, isLoading])

  const verifyPostedData = async payload => {
    try {
      const res = await verifyAccountLeads(payload)
      const { new_account_lead_emails: filterEmails, reseller_sap_ids: filterSapIds } = filterData
      const { new_account_lead_emails: responseEmails, reseller_sap_ids: responseSapIds } = res.data

      const responseEmailsSet = new Set(responseEmails.map(item => item.email))
      const responseSapIdsSet = new Set(responseSapIds.map(item => item.sap_id))

      const arrOfNoEmail = filterEmails.filter(email => !responseEmailsSet.has(email))
      const arrOfNoSapId = filterSapIds.filter(sap_id => !responseSapIdsSet.has(sap_id))

      setArrOfNoEmailFound(arrOfNoEmail)
      setArrOfNoResellerSapIDFound(arrOfNoSapId)

      if ((arrOfNoEmail.length > 0) || (arrOfNoSapId.length > 0)) {
        setIsLoading('error')
      } else if (filterEmails.length === res.data.new_account_lead_emails.length
        && filterSapIds.length === res.data.reseller_sap_ids.length
      ) {
        setIsLoading('success')
      } else {
        setIsLoading('loader')
      }

    } catch (err) {
      setIsLoading('server error')
    }
  }

  return (
    <Modal
      autoHeight
      show={showModal && hasUpload}
      handleClose={() => setShowModal(false)}
    >
      <>
        {(isLoading === 'loading' || isLoading === 'loader') && hasUpload && <LoadingModal showModal={isLoading} />}
        {isLoading === 'error' && hasUpload && (arrOfNoEmailFound.length > 0 || arrOfNoResellerSapIDFound.length > 0) &&
          <WrongFormatModal
            showModal={showModal}
            setShowModal={setShowModal}
            notFoundEmail={arrOfNoEmailFound}
            notFoundResellerSapId={arrOfNoResellerSapIDFound}
          />
        }
        {isLoading === 'server error' && hasUpload &&
          <ServerErrorModal
            showModal={showModal}
            setShowModal={setShowModal}
          />
        }
        {isLoading === 'success' && hasUpload &&
          <SuccessModal
            showModal={showModal}
            setShowModal={setShowModal && closeModel}
            text='Account leads successfully verified'
          />
        }
      </>
    </Modal>
  )
}


const DocUploader = ({
  hasUpload,
  setHasUpload,
  setHasAllRequiredData,
  isVerified,
  setVerified,
  uploadedData,
  setUploadedData,
  tableData,
  setTableData
}) => {
  const [arrOfMissingRequiredVal, setArrOfMissingRequiredVal] = useState([])
  const [showWrongFormatModal, setShowWrongFormatModal] = useState(false)
  const [showWarningModal, setShowWarningModal] = useState(false)
  const [showWarningPrompt, setWarningPrompt] = useState(false)
  const [allAccountLeads, setAllAccountLeads] = useState([])
  const [hasPermission, setHasPermission] = useState(true)
  const [numberOfIssues, setNumberOfIssues] = useState(0)
  const [arrOfDupVals, setArrOfDupVals] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const { hasAccess } = useContext(AuthContext)
  const [structure, setStructure] = useState({
    headers: [],
    dataKeys: []
  })


  useEffect(() => {
    setHasPermission(hasAccess('ANY', 'create-scale-ticket'))
  }, [hasAccess])

  useEffect(() => {
    // testing  purpose i have stored data in local storage
    const curr = JSON.parse(localStorage.getItem('allAccountLeads')) || []
    setAllAccountLeads(curr)
  }, [uploadedData])

  const updateJSONKeys = json => {
    return json.map((obj, index) => {
      for (var oldKey in obj) {
        let newKey = parseHeaders(oldKey)
        if (oldKey !== newKey) {
          Object.defineProperty(obj, newKey,
            Object.getOwnPropertyDescriptor(obj, oldKey))
          delete obj[oldKey]
        }
      }

      const convertToString = obj => {
        const newObj = {}
        Object.entries(obj).forEach(([key, val]) => {
          if (key === 'Date') {
            const convertDate = obj.Date
            return newObj[key] = convertDate ? moment(convertDate).format('MM/DD/YY') : moment(getISODateFromExcel(convertDate)).format('MM/DD/YY')
          }
          return newObj[key] = val.toString()
        })
        return newObj
      }

      return Object.assign({ 'Index': index + 1 }, convertToString(obj))
    })
  }

  const createSingleRow = obj => {
    return {
      'new_account_lead': obj['NEW Account Lead'],
      'new_account_lead_email': obj['NEW Account Lead Email']?.toLowerCase(),
      'old_account_lead': obj['OLD Account Lead'],
      'reseller_sap_id': '00' + obj['Reseller FSAP'],
      'reseller_name': obj['Reseller Name']
    }
  }
  const checkForErrors = json => {
    const setErrorIcon = obj => {
      if (typeof obj.Index === 'number') {
        obj.Index = (<div className='df jcsb'>{obj.Index} {<img className='mr15' alt='error icon' src={error_outline} />}</div>)
      }
    }
    let ticketWithMissingValues = []
    let arrToTestDups = []
    let dupIndexMap = {}
    let arrOfDupIndex = []

    json.forEach((obj, index) => {
      const whatIsMissing = []
      for (let i = 0; i < REQUIRED_HEADERS.length; i++) {
        const currentHeader = REQUIRED_HEADERS[i]
        if (!(currentHeader in obj) || !obj[currentHeader]) {
          obj.customRowClass = 'bgc-red-100'
          whatIsMissing.push(REQUIRED_HEADERS[i])
          setErrorIcon(obj)
        }
        if (i === (REQUIRED_HEADERS.length - 1) && obj.customRowClass !== 'bgc-red-100') {
          dupIndexMap[obj.Index] = createSingleRow(obj)
          arrToTestDups.push(createSingleRow(obj))
        }
      }
      if (whatIsMissing.length) {
        ticketWithMissingValues.push({ index: index + 1, whatIsMissing })
        setErrorIcon(obj)
      }

      return obj
    })

    if (arrToTestDups.length) {
      const results = arrToTestDups.filter(({ new_account_lead: new_account_lead1, new_account_lead_email: new_account_lead_email1, old_account_lead: old_account_lead1, reseller_sap_id: reseller_sap_id1, reseller_name: reseller_name1 }) => (
        allAccountLeads.some(({ new_account_lead: new_account_lead2, new_account_lead_email: new_account_lead_email2, old_account_lead: old_account_lead2, reseller_sap_id: reseller_sap_id2, reseller_name: reseller_name2 }) => (
          new_account_lead1 === new_account_lead2 && new_account_lead_email1 === new_account_lead_email2 && old_account_lead1 === old_account_lead2 && reseller_sap_id1 === reseller_sap_id2 && reseller_name1 === reseller_name2
        ))))


      // Function to find duplicates based on sap_id and email
      const findDuplicates = arr => {
        const seen = {}
        const duplicates = []

        arr.forEach((obj, index) => {
          obj = JSON.parse(obj)
          const key = `${obj.reseller_sap_id}_${obj.new_account_lead_email}`

          if (seen[key]) duplicates.push(JSON.stringify(obj))
          else seen[key] = true

        })
        return duplicates
      }
      const stringfyArr = arrToTestDups.map(obj => JSON.stringify(obj))
      let parsedResults = findDuplicates(stringfyArr).map(obj => JSON.parse(obj))
      parsedResults = [...parsedResults, ...results]

      arrOfDupIndex = parsedResults.map(obj => {
        let returnVal = []
        for (const [key] of Object.entries(dupIndexMap)) {
          if (dupIndexMap[key].reseller_sap_id == obj.reseller_sap_id) {
            returnVal.push(key)
          }
        }
        return returnVal
      })
    }
    if (arrOfDupIndex.length) {
      arrOfDupIndex = _.flatten(arrOfDupIndex)
    }

    arrOfDupIndex.forEach(dupeIndex => {
      json.forEach(scaleTickObj => {
        if (scaleTickObj.Index.toString() === dupeIndex) {
          scaleTickObj.customRowClass = 'bgc-red-100'
          scaleTickObj.Index = (<div className='df jcsb'>{scaleTickObj.Index} {<img className='mr15' alt='error icon' src={error_outline} />}</div>)
        }
      })
    })
    arrOfDupIndex = [...new Set(arrOfDupIndex)]
    return {
      ticketWithMissingValues,
      arrOfDupIndex
    }
  }

  const renderJSON = JSONobj => {
    const headers = [...new Set(JSONobj.flatMap(Object.keys))]
    let headerArr = [
      {
        value: 'Index',
        format() {
          return (<div key='space' className='cell df aic' />)
        }
      }
    ]
    let dataArr = ['Index']
    headers.forEach(header => {
      let parsedHeader = parseHeaders(header)
      headerArr.push({
        value: parsedHeader,
        format: () => headerCell(parsedHeader)
      })
      dataArr.push(parsedHeader)
    })

    JSONobj = updateJSONKeys(JSONobj)
    let errorObj = checkForErrors(JSONobj)
    if (errorObj?.ticketWithMissingValues?.length || errorObj?.arrOfDupIndex?.length || errorObj?.ticketWithInvalidProductionYear?.length) {
      setArrOfMissingRequiredVal(errorObj.ticketWithMissingValues)
      setArrOfDupVals(errorObj.arrOfDupIndex)
      setNumberOfIssues((errorObj.ticketWithMissingValues.length || 0) + (errorObj.arrOfDupIndex.length))
      setWarningPrompt(true)
    } else {
      setHasAllRequiredData(true)
    }

    const filterData = JSONobj.map(curr => {
      const currObj = createSingleRow(curr)
      currObj['Index'] = curr.Index
      return currObj
    })

    setTableData(JSONobj)
    setUploadedData(filterData)

    const setUpStruc = {
      headers: headerArr,
      dataKeys: dataArr
    }
    setStructure(setUpStruc)
  }

  const onDrop = (acceptedFiles, rejectedFiles) => {
    if (rejectedFiles.length > 0) {
      setShowWrongFormatModal(true)
    } else {
      setIsLoading(true)
      const reader = new FileReader()
      acceptedFiles.forEach(file => {
        reader.onload = () => {
          const fileAsBinaryString = reader.result
          if (acceptedFiles[0] && acceptedFiles[0]?.type.endsWith('csv')) {
            csv({
              noheader: true,
              output: 'json'
            })
              .fromString(fileAsBinaryString)
              .then(csvRows => {
                const toJSON = []
                csvRows.forEach((aCsvRow, i) => {
                  if (i !== 0) {
                    const builtObject = {}
                    Object.keys(aCsvRow).forEach(aKey => {
                      const valueToAddInBuiltObject = aCsvRow[aKey]
                      const keyToAddInBuiltObject = csvRows[0][aKey]
                      builtObject[keyToAddInBuiltObject] = valueToAddInBuiltObject
                    })
                    toJSON.push(builtObject)
                  }
                })
                renderJSON(toJSON)
              })
          } else if (acceptedFiles[0]?.type.endsWith('sheet') || acceptedFiles[0]?.path.endsWith('xls')) {

            let workBook = XLSX.read(fileAsBinaryString, { type: 'binary', cellDates: true, cellNF: false, cellText: false })
            let jsonData = workBook.SheetNames.reduce((init, name) => {
              const sheet = workBook.Sheets[name]
              return XLSX.utils.sheet_to_json(sheet, { raw: true, dateNF: 'MM-DD-YY', defval: '' })
            }, {})
            renderJSON(jsonData)
          }
          setHasUpload(true)
          setIsLoading(false)
        }
        reader.readAsBinaryString(file)
      })
    }
  }

  const parseHeaders = header => {
    let returnHeader
    const normalizeHeader = header.toLowerCase().trim().split('.').join('')
    for (const [headerName, headerArray] of Object.entries(ACCEPTABLE_HEADERS)) {
      if (headerArray.includes(normalizeHeader)) {
        returnHeader = headerName
      }
    }
    return returnHeader ? returnHeader : header
  }

  const clear = () => {
    setTableData()
    setStructure({
      headers: [],
      dataKeys: []
    })
    setVerified(false)
    setArrOfDupVals([])
    setHasAllRequiredData(false)
    setArrOfMissingRequiredVal([])
    setHasUpload(false)
    setWarningPrompt(false)
    setNumberOfIssues(0)
    setUploadedData([])

  }

  const { open, getRootProps, getInputProps } = useDropzone({ onDrop, noClick: true, noKeyboard: true, accept: '.csv, .xls, .xlsx' })

  const prompt = (
    <div {...getRootProps()}>
      {<p>Drag-and-drop or choose a file to import linked contracts <br /> Supported file types: .csv, .xls, or .xlsx</p>}
      <input {...getInputProps()} />
      <Button text='Choose File' onClick={open} />
    </div>)

  const ButtonRow = () => {
    return (
      <div className='df mt25'>
        <div className='fxdrc aifs'>
          {tableData
            ? <Button text='Clear File' onClick={clear} />
            : <>
              <div className='mb5 fz15'>Upload CSV or XLS</div>
              <Button text='Choose File' onClick={open} />
            </>
          }
        </div>
        {
          showWarningPrompt ?
            <div className='mauto bd1-s-red bgc-red-100 mb20'>
              <div className='df pt15 pr10 pl10 pb15'>
                <img
                  alt='error icon'
                  src={error} />
                <div className='pr5 pl5 mt5'>
                  {`${numberOfIssues} issue${numberOfIssues > 1 ? 's' : ''} found. Edit and reupload the corrected source file in order to save.`}
                </div>
                <Button customClassName='m0' text={'Review Issue'} clickHandler={() => setShowWarningModal(true)}></Button>
              </div>
            </div>
            : null
        }
      </div>
    )
  }

  return (
    <>
      {
        hasPermission ?
          <div>
            <ButtonRow />
            <WarningModal arrOfDupVals={arrOfDupVals} showModal={showWarningModal} setShowModal={setShowWarningModal} numberOfIssues={numberOfIssues} arrOfMissingRequiredVal={arrOfMissingRequiredVal} />
            <WrongFormatModal showWrongFormatModal={showWrongFormatModal} setShowWrongFormatModal={setShowWrongFormatModal} />
            {isVerified && <DataUploadingModal showModal={isVerified} setShowModal={setVerified} data={uploadedData} clear={clear} closeModel={setHasUpload} hasUpload={hasUpload} />}
            <div className='mt20'>
              {isLoading ?
                <div className='h100% w100% df aic jcc mt40'>
                  <div className='loader h80 w80' />
                </div>
                : tableData ? <div className="scale_ticket_sec">
                  <Table data={tableData} structure={structure} />
                </div>
                  : <div className="scale_ticket_before_sec"><Table statusComponentHeight='h400' structure={initStructure} statusComponent={<TableStatus status={'EMPTY'} prompt={prompt} />} /></div>
              }
            </div>
          </div>
          : <AccessDeniedView />
      }
    </>
  )
}

export default DocUploader
